import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { ref, get, set, update } from "firebase/database";
import Popup from "./Popup";

const AuctionSettings = ({ gameId }) => {
  const [form, setForm] = useState({
    sport: "",
    totalTeams: "",
    purseValue: "",
    totalPlayers: "",
    unsoldAction: "Re-auction",
    maxAmountPerPlayer: "",
    minBidIncrement: "",
    defaultBasePrice: "",
  });

  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' });

  useEffect(() => {
    if (!gameId) return;

    const loadSettings = async () => {
      try {
        const settingsRef = ref(db, `games/${gameId}/auctionSettings`);
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          setForm(snapshot.val());
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      } finally {
        setInitialLoad(false);
      }
    };

    loadSettings();
  }, [gameId]);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!gameId) {
      setPopup({
        isOpen: true,
        title: 'Missing Game ID',
        message: "Game ID is missing.",
        type: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      // Save auction settings
      const settingsRef = ref(db, `games/${gameId}/auctionSettings`);
      await set(settingsRef, form);

      // Update game details with total purse
      await update(ref(db, `games/${gameId}/details`), {
        totalPurse: Number(form.purseValue),
      });

      setPopup({
        isOpen: true,
        title: 'Settings Saved',
        message: "Auction settings saved successfully!",
        type: 'success'
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      setPopup({
        isOpen: true,
        title: 'Save Failed',
        message: "Error saving auction settings: " + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="card shadow">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow">
      <div className="card-header bg-primary text-white">
        <h4 className="mb-0">
          <i className="bi bi-gear-fill me-2"></i>
          Auction Settings
        </h4>
      </div>

      <div className="card-body p-4">
        <form onSubmit={handleSubmit}>
          {/* Sport Selection */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="sport" className="form-label fw-semibold">
                <i className="bi bi-trophy me-2 text-warning"></i>
                Select Sport <span className="text-danger">*</span>
              </label>
              <select
                id="sport"
                className="form-select form-select-lg"
                value={form.sport}
                required
                onChange={(e) => handleChange("sport", e.target.value)}
              >
                <option value="">-- Select Sport --</option>
                <option value="Cricket">🏏 Cricket</option>
                <option value="Football">⚽ Football</option>
                <option value="Basketball">🏀 Basketball</option>
                <option value="Volleyball">🏐 Volleyball</option>
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="totalTeams" className="form-label fw-semibold">
                <i className="bi bi-people-fill me-2 text-info"></i>
                Total Teams <span className="text-danger">*</span>
              </label>
              <input
                id="totalTeams"
                className="form-control form-control-lg"
                type="number"
                min="2"
                max="20"
                required
                placeholder="e.g., 8"
                value={form.totalTeams}
                onChange={(e) => handleChange("totalTeams", e.target.value)}
              />
              <small className="text-muted">Minimum 2 teams required</small>
            </div>
          </div>

          <hr className="my-4" />

          {/* Financial Settings */}
          <h5 className="fw-bold mb-3">
            <i className="bi bi-cash-stack me-2 text-success"></i>
            Financial Settings
          </h5>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="purseValue" className="form-label fw-semibold">
                Default Team Purse <span className="text-danger">*</span>
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-success text-white">৳</span>
                <input
                  id="purseValue"
                  className="form-control"
                  type="number"
                  min="1"
                  required
                  placeholder="1000000"
                  value={form.purseValue}
                  onChange={(e) => handleChange("purseValue", e.target.value)}
                />
              </div>
              <small className="text-muted">Initial budget for each team</small>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="maxAmountPerPlayer" className="form-label fw-semibold">
                Max Amount Per Player
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-warning">৳</span>
                <input
                  id="maxAmountPerPlayer"
                  className="form-control"
                  type="number"
                  min="0"
                  placeholder="Optional (e.g., 200000)"
                  value={form.maxAmountPerPlayer}
                  onChange={(e) =>
                    handleChange("maxAmountPerPlayer", e.target.value)
                  }
                />
              </div>
              <small className="text-muted">Leave empty for no limit</small>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="minBidIncrement" className="form-label fw-semibold">
                Minimum Bid Increment <span className="text-danger">*</span>
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-info text-white">৳</span>
                <input
                  id="minBidIncrement"
                  className="form-control"
                  type="number"
                  min="100"
                  required
                  placeholder="e.g., 1000"
                  value={form.minBidIncrement}
                  onChange={(e) =>
                    handleChange("minBidIncrement", e.target.value)
                  }
                />
              </div>
              <small className="text-muted">Minimum amount to increase each bid</small>
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="playerBasePrice" className="form-label fw-semibold">
                Player Base Price <span className="text-danger">*</span>
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-info text-white">৳</span>
                <input
                  id="playerBasePrice"
                  className="form-control"
                  type="number"
                  min="100"
                  required
                  placeholder="e.g., 1000"
                  value={form.defaultBasePrice}
                  onChange={(e) =>
                    handleChange("defaultBasePrice", e.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <hr className="my-4" />

          {/* Player Settings */}
          <h5 className="fw-bold mb-3">
            <i className="bi bi-person-badge me-2 text-primary"></i>
            Player Settings
          </h5>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="totalPlayers" className="form-label fw-semibold">
                Total Players <span className="text-danger">*</span>
              </label>
              <input
                id="totalPlayers"
                className="form-control form-control-lg"
                type="number"
                min="1"
                required
                placeholder="e.g., 15"
                value={form.totalPlayers}
                onChange={(e) => handleChange("totalPlayers", e.target.value)}
              />
              <small className="text-muted">Maximum squad size</small>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="unsoldAction" className="form-label fw-semibold">
                Unsold Players Action <span className="text-danger">*</span>
              </label>
              <select
                id="unsoldAction"
                className="form-select form-select-lg"
                value={form.unsoldAction}
                onChange={(e) => handleChange("unsoldAction", e.target.value)}
              >
                <option value="Re-auction">🔄 Re-auction</option>
                <option value="Remove from auction">❌ Remove from auction</option>
                <option value="Keep for later">⏳ Keep for later</option>
              </select>
              <small className="text-muted">What to do with unsold players</small>
            </div>
          </div>

          {/* Summary Card */}
          <div className="alert alert-light border mt-4">
            <h6 className="alert-heading">
              <i className="bi bi-info-circle me-2"></i>
              Settings Summary
            </h6>
            <ul className="mb-0">
              <li>
                <strong>Sport:</strong> {form.sport || "Not selected"}
              </li>
              <li>
                <strong>Total Teams:</strong> {form.totalTeams || "Not set"}
              </li>
              <li>
                <strong>Purse per Team:</strong> ৳
                {form.purseValue
                  ? Number(form.purseValue).toLocaleString()
                  : "Not set"}
              </li>
              <li>
                <strong>Max Players:</strong> {form.totalPlayers || "Not set"}
              </li>
              <li>
                <strong>Max per Player:</strong>{" "}
                {form.maxAmountPerPlayer
                  ? `৳${Number(form.maxAmountPerPlayer).toLocaleString()}`
                  : "Unlimited"}
              </li>
              <li>
                <strong>Min Bid Increment:</strong>{" "}
                {form.minBidIncrement
                  ? `৳${Number(form.minBidIncrement).toLocaleString()}`
                  : "Not set"}
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="d-grid gap-2 mt-4">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving Settings...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Save Auction Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', type: 'info' })}
        message={popup.message}
        type={popup.type}
      />
    </div>
  );
};

export default AuctionSettings;